import { DATE_FORMAT } from '@/constants/common.constant'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { IGfrRiskHistoryDTO } from '@/interfaces/gfrReport.interface'
import { formatNumber } from '@/utils/utils'
import { Box } from '@mui/material'
import { getColumnDetail } from '@utils/grid-utils/columnUtils'
import { formatCellValue } from '@utils/tableGridUtils'
import dayjs from 'dayjs'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useEffect, useState } from 'react'

export type IGFPASummary = {
  chartData: IGfrRiskHistoryDTO[]
}

export const GFRSummaryChart: React.FunctionComponent<IGFPASummary> = ({
  chartData,
}) => {
  const layout = useAppSelector((s) => s.verticallySplitLayout?.contentFocus)
  const [chartOptions, setChartOptions] = useState({
    chart: {
      height: 260,
    },
    title: {
      text: null,
    },
    xAxis: {},
    yAxis: [
      {
        title: {
          text: 'Risk (%)',
        },
      },
    ],
    legend: {
      align: 'right',
      verticalAlign: 'top',
      layout: 'vertical',
      itemMarginTop: 5,
      itemMarginBottom: 5,
    },
    series: [],
    credits: {
      enabled: false,
    },
    plotOptions: {
      series: {
        keys: ['y', 'custom.value'],
      },
    },
    tooltip: {
      headerFormat: '<p>Date: {point.x}</p><br/>',
      pointFormat:
        '<p>{series.name}: <strong>{point.options.custom.value}</strong></p>',
    },
  })

  useEffect(() => {
    const portFolioData = {
      name: getColumnDetail('portfolioTotalRisk', AxiomReportType.GFR).header,
      data: [] as [number, string][],
      marker: {
        enabled: false,
      },
    }
    const benchMarkData = {
      name: getColumnDetail('benchmarkTotalRisk', AxiomReportType.GFR).header,
      data: [] as [number, string][],
      marker: {
        enabled: false,
      },
    }
    const activeData = {
      name: getColumnDetail('activeTotalRisk', AxiomReportType.GFR).header,
      data: [] as [number, string][],
      marker: {
        enabled: false,
      },
    }
    const categories: string[] = []

    chartData &&
      chartData.length &&
      chartData.forEach((data: IGfrRiskHistoryDTO) => {
        portFolioData.data.push([
          Number(formatNumber(data.portfolioTotalRisk)),
          formatCellValue(
            formatNumber(data.portfolioTotalRisk),
            getColumnDetail('portfolioTotalRisk', AxiomReportType.GFR).format,
          ),
        ])
        benchMarkData.data.push([
          Number(formatNumber(data.benchmarkTotalRisk)),
          formatCellValue(
            formatNumber(data.benchmarkTotalRisk),
            getColumnDetail('benchmarkTotalRisk', AxiomReportType.GFR).format,
          ),
        ])
        activeData.data.push([
          Number(formatNumber(data.activeTotalRisk)),
          formatCellValue(
            formatNumber(data.activeTotalRisk),
            getColumnDetail('activeTotalRisk', AxiomReportType.GFR).format,
          ),
        ])
        categories.push(
          dayjs(data.analysisDate).format(DATE_FORMAT.TABLE_CHART),
        )
      })

    portFolioData.marker.enabled = portFolioData.data.length < 5
    benchMarkData.marker.enabled = benchMarkData.data.length < 5
    activeData.marker.enabled = activeData.data.length < 5

    const updatedChartOptions: any = { ...chartOptions }
    updatedChartOptions.xAxis = { categories: categories }
    updatedChartOptions.series[0] = portFolioData
    updatedChartOptions.series[1] = benchMarkData
    updatedChartOptions.series[2] = activeData
    setChartOptions(updatedChartOptions)
  }, [chartData])

  useEffect(() => {
    const chartHeight = layout?.isTopExpanded ? 640 : 260
    setChartOptions((prevChartOptions) => ({
      ...prevChartOptions,
      chart: {
        ...prevChartOptions.chart,
        height: chartHeight,
      },
    }))
  }, [layout])

  return (
    <Box display='grid' data-testid='chart'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </Box>
  )
}
